/**
 * Landing-page copy + section data.
 *
 * Single source of truth for the public marketing surface. All copy
 * is original to ReadyRoomOps — the visual structure mirrors the
 * Director's sister product (NoRework) but the words, the verticals,
 * and the value props are specific to room readiness, not inspection
 * compliance.
 *
 * The three property verticals (dorm / office / property_mgmt) match
 * the SQL CHECK constraint in `public.leads.vertical` exactly — drift
 * here would silently send the Edge Function a value it rejects.
 */

import type { Vertical, InquiryType } from '@/types/landing'

// ─────────────────────────────────────────────────────────────────────
// Verticals
// ─────────────────────────────────────────────────────────────────────

export const VERTICALS: readonly Vertical[] = ['dorm', 'office', 'property_mgmt']

export const VERTICAL_LABELS: Record<Vertical, string> = {
  dorm:           'Dorms',
  office:         'Offices',
  property_mgmt:  'Property Managers',
}

export const VERTICAL_LONG_LABELS: Record<Vertical, string> = {
  dorm:           'Dorms & Residence Halls',
  office:         'Office Buildings',
  property_mgmt:  'Property Managers',
}

export interface VerticalCopy {
  /** One-line subhead under the hero headline, swapped per chip. */
  heroLine:    string
  /** Phrasing for the form's rooms-count input. */
  roomsLabel:  string
}

export const VERTICAL_COPY: Record<Vertical, VerticalCopy> = {
  dorm: {
    heroLine:   'Turn 800 dorm rooms over the weekend with photo proof on every one.',
    roomsLabel: 'Approximate # of rooms in your portfolio',
  },
  office: {
    heroLine:   'Daily janitorial proof — cleaner ID, timestamp, photo on every room.',
    roomsLabel: 'Approximate # of rooms in the building',
  },
  property_mgmt: {
    heroLine:   'Multi-property turnover from one dashboard. Same audit trail, every unit.',
    roomsLabel: 'Approximate # of properties you manage',
  },
}

// ─────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────

export const HERO_BADGE      = 'ROOM-READINESS SOFTWARE'
export const HERO_HEADLINE_1 = 'Ready every room'
export const HERO_HEADLINE_2 = 'the first time.'
export const HERO_TAGLINE    =
  'Photo proof, AI verification, and an immutable audit trail on every clean. Built for the field, not the office.'

// ─────────────────────────────────────────────────────────────────────
// Why teams choose ReadyRoomOps  (3 cards, cream → orange/green/black tiles)
// ─────────────────────────────────────────────────────────────────────

export type WhyTone = 'orange' | 'green' | 'black'

export interface WhyCard {
  tone:  WhyTone
  /** Lucide icon name as a string — resolved in the component. */
  icon:  'Clock' | 'ShieldCheck' | 'BadgeDollarSign'
  title: string
  body:  string
}

export const WHY_HEADLINE = 'Why teams pick ReadyRoomOps'
export const WHY_SUBHEAD  = 'Built for the field, not the office.'

export const WHY_CARDS: readonly WhyCard[] = [
  {
    tone:  'orange',
    icon:  'Clock',
    title: 'Save Time',
    body:  "Cleaners get a phone-first checklist that takes minutes. Supervisors get a board, not a clipboard. Approvals are one tap, not one walkthrough.",
  },
  {
    tone:  'green',
    icon:  'ShieldCheck',
    title: 'Pass Inspection',
    body:  "Photo proof on every room. AI vision flags rooms that aren't actually clean before a supervisor signs off. Audit trail on every status change — SOC 2 ready out of the box.",
  },
  {
    tone:  'black',
    icon:  'BadgeDollarSign',
    title: 'Stop Paying for Rework',
    body:  "Maintenance issues block readiness automatically. Rooms can't be marked ready until everything's actually fixed. No more move-in surprises that cost real money.",
  },
]

// ─────────────────────────────────────────────────────────────────────
// How it works  (4 numbered steps with watermark numerals)
// ─────────────────────────────────────────────────────────────────────

export interface HowStep {
  step:  '01' | '02' | '03' | '04'
  icon:  'ClipboardList' | 'Smartphone' | 'Camera' | 'CheckCircle2'
  title: string
  body:  string
}

export const HOW_HEADLINE = 'How it works'
export const HOW_SUBHEAD  = 'From dirty to ready in four steps — every step audited.'

export const HOW_STEPS: readonly HowStep[] = [
  {
    step:  '01',
    icon:  'ClipboardList',
    title: 'Assign the room',
    body:  "Auto-generated from move-out events, the supervisor's queue, or a one-tap manual create. Cleaners see only what's theirs, sorted by urgency.",
  },
  {
    step:  '02',
    icon:  'Smartphone',
    title: 'Cleaner does the checklist',
    body:  "Phone-first, offline-capable. Photos upload with HEIC handling, EXIF stripping, and image compression. Works on or off Wi-Fi.",
  },
  {
    step:  '03',
    icon:  'Camera',
    title: 'Photo + AI verify',
    body:  "Anthropic Claude Haiku checks each photo against the checklist requirement. Flags rooms that look clean but aren't. Cleaner gets coaching, supervisor gets confidence.",
  },
  {
    step:  '04',
    icon:  'CheckCircle2',
    title: 'Supervisor approves',
    body:  "One tap on the supervisor dashboard. Room transitions to ready. Audit log records who, when, and why — immutable, append-only, SOC-2-ready.",
  },
]

// ─────────────────────────────────────────────────────────────────────
// Features grid  (dark section, 6 cards, all orange tiles)
// ─────────────────────────────────────────────────────────────────────

export interface FeatureCard {
  icon:  'Image' | 'Eye' | 'WifiOff' | 'Lock' | 'FileSearch' | 'Fingerprint'
  title: string
  body:  string
}

export const FEATURES_HEADLINE = 'Built for the field'
export const FEATURES_SUBHEAD  =
  "Everything below is shipped, audited, and live in production today — not a roadmap."

export const FEATURE_CARDS: readonly FeatureCard[] = [
  {
    icon:  'Image',
    title: 'Photo verification',
    body:  "Every checklist item gets a photo. Every photo is signed-URL gated to the cleaner's org. Cleaners can't access another tenant's photos by guessing paths.",
  },
  {
    icon:  'Eye',
    title: 'AI vision',
    body:  "Claude Haiku 4.5 reviews submitted photos against the requirement. Returns a verdict the supervisor sees inline — no separate dashboard, no review queue.",
  },
  {
    icon:  'WifiOff',
    title: 'Offline mode',
    body:  "Full PWA. Mutations queue offline. Background-sync flushes the moment connectivity returns. No app-store install, no friction for cleaners.",
  },
  {
    icon:  'Lock',
    title: 'Multi-tenant + RLS',
    body:  "Every read is scoped at the database layer. Cross-tenant data leakage is structurally impossible — not a runtime check the application could forget.",
  },
  {
    icon:  'FileSearch',
    title: 'Append-only audit',
    body:  "Every status change, photo upload, approval, and rejection lands in an immutable audit log. Partitioned by month, retained per your compliance schedule.",
  },
  {
    icon:  'Fingerprint',
    title: 'Biometric + PIN sign-in',
    body:  "Admin-provisioned PINs bootstrap the first sign-in. Biometric (Face ID, Windows Hello, Android fingerprint) takes over from there. No passwords, anywhere.",
  },
]

// ─────────────────────────────────────────────────────────────────────
// Who it's for  (4 personas)
// ─────────────────────────────────────────────────────────────────────

export interface PersonaCard {
  icon:  'GraduationCap' | 'Building2' | 'Home' | 'Sparkles'
  title: string
  body:  string
}

export const PERSONAS_HEADLINE = "Who it's for"
export const PERSONAS_SUBHEAD  =
  'From the field to the front office, every role sees the same room state.'

export const PERSONAS: readonly PersonaCard[] = [
  {
    icon:  'GraduationCap',
    title: 'Residence-life teams',
    body:  "Turn hundreds of rooms over a single move-in weekend. Photo proof on every one, audit trail SOC-2-ready, no clipboards.",
  },
  {
    icon:  'Building2',
    title: 'Office facilities managers',
    body:  "Daily janitorial proof — cleaner ID + timestamp + photo on every room. No more paper sign-off sheets, no more 'did it actually get cleaned?'",
  },
  {
    icon:  'Home',
    title: 'Property managers',
    body:  "Multi-property turnover from one dashboard. Vacation rental, long-term, mixed portfolio — same checklist, same audit trail.",
  },
  {
    icon:  'Sparkles',
    title: 'Cleaning contractors',
    body:  "One login across every property. Show your work with photo proof. Get paid faster — supervisors approve in real time, not at week's end.",
  },
]

// ─────────────────────────────────────────────────────────────────────
// Pricing  (single card, "Contact for quote" instead of NoRework's $6,500)
// ─────────────────────────────────────────────────────────────────────

export const PRICING_HEADLINE = 'One platform. One audit trail.'
export const PRICING_SUBHEAD  =
  "Pricing scales with your portfolio. Tell us your room count and we'll send a quote that fits."

export const PRICING_BADGE     = 'TEAM LICENSE'
export const PRICING_TITLE     = 'ReadyRoomOps Platform'
export const PRICING_PRICE     = 'Contact for quote'
export const PRICING_PRICE_SUB =
  'No published tiers. Tell us your portfolio and we send a number that fits.'

export const PRICING_FEATURES: readonly string[] = [
  'Unlimited rooms, buildings, and floors',
  'All three property verticals (dorms / offices / property managers)',
  'Photo verification + Claude Haiku AI vision',
  'Offline-capable PWA on every device',
  'Append-only audit trail (SOC 2 ready)',
  'Biometric + PIN sign-in (no passwords)',
  'Multi-tenant RLS at the database layer',
  'Email notifications + Web Push',
]

// ─────────────────────────────────────────────────────────────────────
// Inquiry-type labels  (used in the form)
// ─────────────────────────────────────────────────────────────────────

export const INQUIRY_LABEL: Record<InquiryType, string> = {
  quote:    'Send me a quote',
  info:     'Tell me more about the product',
  demo:     'Schedule a demo',
  waitlist: 'Add me to the waitlist',
}
